import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { trpc } from '../../lib/trpc';
import { PageContainer } from '../../components/PageContainer';
import { WineRow } from '../../components/WineRow';

type SortMode = 'varietal' | 'vintage' | 'price';

const SORT_LABELS: Record<SortMode, string> = {
  varietal: 'Varietal',
  vintage: 'Vintage',
  price: 'Price',
};

interface WineListItem {
  _id: string;
  name: string;
  vintage?: number;
  varietal?: string;
  appellation?: string;
  price?: number;
}

export const WineryWines = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = (searchParams.get('sort') as SortMode) ?? 'varietal';

  const venueQuery = trpc.venue.getById.useQuery({ id: id ?? '' }, { enabled: Boolean(id) });
  const winesQuery = trpc.wine.listByVenue.useQuery(
    { venueId: id ?? '', limit: 100 },
    { enabled: Boolean(id) }
  );

  const wines = useMemo(
    () => (winesQuery.data?.items ?? []) as WineListItem[],
    [winesQuery.data]
  );

  const wishlistMatchQuery = trpc.user.wishlistMatchForVenue.useQuery(
    { venueId: id ?? '' },
    { enabled: Boolean(id) }
  );
  const wishlistMatchCount = wishlistMatchQuery.data?.count ?? 0;
  const wishlistOnly = searchParams.get('filter') === 'wishlist' && wishlistMatchCount > 0;

  const filteredWines = useMemo(() => {
    if (!wishlistOnly) return wines;
    const ids = new Set(wishlistMatchQuery.data?.wineIds ?? []);
    return wines.filter((w) => ids.has(w._id));
  }, [wines, wishlistOnly, wishlistMatchQuery.data]);

  const grouped = useMemo(() => {
    if (sort !== 'varietal') return null;
    const groups = new Map<string, typeof filteredWines>();
    for (const wine of filteredWines) {
      const key = wine.varietal?.trim() || 'Other';
      const existing = groups.get(key) ?? [];
      existing.push(wine);
      groups.set(key, existing);
    }
    // Sort vintages within each group, newest first
    for (const [key, group] of groups) {
      groups.set(
        key,
        [...group].sort((a, b) => (b.vintage ?? 0) - (a.vintage ?? 0))
      );
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [sort, filteredWines]);

  const flatSorted = useMemo(() => {
    if (sort === 'vintage') {
      return [...filteredWines].sort((a, b) => (b.vintage ?? 0) - (a.vintage ?? 0));
    }
    if (sort === 'price') {
      return [...filteredWines].sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
    }
    return filteredWines;
  }, [sort, filteredWines]);

  const setSort = (mode: SortMode) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', mode);
    setSearchParams(next, { replace: true });
  };

  if (!id) return null;

  const vintageCount = new Set(wines.map((w) => w.vintage).filter(Boolean)).size;
  const countLine = `${wines.length} wine${wines.length === 1 ? '' : 's'} across ${vintageCount} vintage${vintageCount === 1 ? '' : 's'}`;

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl 2xl:max-w-5xl mx-auto">
      <PageContainer className="space-y-8 mt-2">
        <div>
          <div className="text-sm text-gray-600 mb-1">
            <Link to={`/wineries/${id}`} className="hover:text-primary-700">
              {venueQuery.data?.name ?? '...'}
            </Link>
            {'  ›'}
          </div>
          <h1 className="font-serif text-3xl text-gray-900 mb-1">Wines</h1>
          {wines.length > 0 && (
            <p className="text-sm text-gray-600">{countLine}</p>
          )}
        </div>

        {wines.length > 0 && (
          <div role="group" aria-label="Sort by" className="inline-flex bg-gray-100 rounded-md p-1">
            {(Object.keys(SORT_LABELS) as SortMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setSort(mode)}
                aria-pressed={sort === mode}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  sort === mode
                    ? 'bg-gray-50 text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {SORT_LABELS[mode]}
              </button>
            ))}
          </div>
        )}

        {wishlistMatchCount > 0 && (
          <div className="flex gap-2 text-sm">
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('filter');
                setSearchParams(next, { replace: true });
              }}
              className={`px-3 py-1 rounded-full transition-colors ${
                !wishlistOnly
                  ? 'bg-primary-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Showing all ({wines.length})
            </button>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set('filter', 'wishlist');
                setSearchParams(next, { replace: true });
              }}
              className={`px-3 py-1 rounded-full transition-colors ${
                wishlistOnly
                  ? 'bg-primary-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Wishlist only ({wishlistMatchCount})
            </button>
          </div>
        )}

        {filteredWines.length === 0 ? (
          <p className="text-gray-600">
            {wishlistOnly
              ? 'None of your wishlist wines are poured here.'
              : `No wines listed yet for ${venueQuery.data?.name ?? 'this venue'}.`}
          </p>
        ) : grouped ? (
          <div className="space-y-8">
            {grouped.map(([varietal, group]) => (
              <section key={varietal}>
                <div className="border-t border-gray-300 pt-2 mb-3">
                  <h2 className="font-serif text-xl text-gray-900">{varietal}</h2>
                  <div className="font-sans text-xs uppercase tracking-widest text-gray-600">
                    {group.length} vintage{group.length === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="border-t border-gray-200">
                  {group.map((wine) => (
                    <WineRow
                      key={wine._id}
                      wine={{
                        _id: wine._id,
                        name: wine.name,
                        vintage: wine.vintage,
                        varietal: undefined,
                        appellation: wine.appellation,
                        price: wine.price,
                        venueId: null,
                      }}
                      hideWineryName
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="border-t border-gray-200">
            {flatSorted.map((wine) => (
              <WineRow
                key={wine._id}
                wine={{
                  _id: wine._id,
                  name: wine.name,
                  vintage: wine.vintage,
                  varietal: wine.varietal,
                  appellation: wine.appellation,
                  price: wine.price,
                  venueId: null,
                }}
                hideWineryName
              />
            ))}
          </div>
        )}
      </PageContainer>
    </div>
  );
};
